import { useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Video, Download, Sparkles, Film } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { API } from "../config";
import { Link } from "react-router-dom";

/**
 * Parse a generated_content string for SECTION 5 clip suggestions from the
 * podcast-repurposing SEO template. Returns an array of:
 *   { index: 1-based, range: "03:20–03:45", caption: "...", rationale: "..." }
 *
 * Tolerant to small LLM formatting drifts (hyphens, em-dashes, missing colons).
 */
export function parsePodcastClips(text) {
  if (!text || typeof text !== "string") return [];

  // Strategy: prefer the Section-5 anchored block if it's there (cleaner), but
  // fall back to scanning the WHOLE text for "Clip N — ..." patterns when the
  // LLM omitted the section header. The fallback handles real-world drift in
  // GPT output without needing to retrain the podcast template.
  let block;
  const sec5Match = text.match(/SECTION\s*5[^\n]*\n([\s\S]*?)(?=\n=== SECTION|\n=== |$)/i);
  if (sec5Match) {
    block = sec5Match[1];
  } else if (/\bClip\s*\d+\s*[—–-]/i.test(text)) {
    // No section header found, but clip rows exist — scan the whole text
    block = text;
  } else {
    return [];
  }

  const clips = [];
  const lines = block.split(/\n/);
  let current = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const clipHead = line.match(/^Clip\s*(\d+)\s*[—–-]\s*(.+)$/i);
    if (clipHead) {
      if (current) clips.push(current);
      current = { index: parseInt(clipHead[1], 10), range: clipHead[2].trim(), caption: "", rationale: "" };
      continue;
    }
    if (!current) continue;
    const cap = line.match(/^Caption\s*:?\s*(.+)$/i);
    if (cap) { current.caption = cap[1].trim(); continue; }
    const why = line.match(/^Why\s*it\s*works\s*:?\s*(.+)$/i);
    if (why) { current.rationale = why[1].trim(); continue; }
    if (current.rationale) current.rationale += " " + line;
    else if (current.caption) current.caption += " " + line;
  }
  if (current) clips.push(current);

  return clips.filter(c => c.caption || c.range);
}

/**
 * Per-clip Sora 2 generation. Each clip kicks off its own video job and is
 * tracked independently. The clip is NOT linked back to the parent content
 * doc (parent only carries a single video_url slot), so the user finds the
 * finished MP4 on /dashboard/videos as well as inline here.
 */
export default function PodcastClipReels({ content, userPlan, token }) {
  // Depend on BOTH the id and the body string so a remount or content swap
  // always re-parses, never serves a stale memoised result.
  const clips = useMemo(
    () => parsePodcastClips(content?.generated_content),
    [content?.id, content?.generated_content]
  );
  const [jobs, setJobs] = useState({});

  // Heuristic: did the body LOOK like it should contain clips even if the
  // parser couldn't extract them cleanly? If so, render a small diagnostic
  // strip instead of silently returning null — that's what made the panel
  // "disappear" on a second open and triggered a confusing UX.
  const hasClipShapedText = useMemo(
    () => !!content?.generated_content && /\bClip\s*\d+\s*[—–-]/i.test(content.generated_content),
    [content?.id, content?.generated_content]
  );

  if (!clips.length) {
    if (!hasClipShapedText) return null;
    return (
      <div className="px-4 py-3 border-t border-border bg-amber-500/5" data-testid="podcast-clip-reels-fallback">
        <div className="flex items-center gap-2 mb-1">
          <Film className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-300">Clip suggestions detected but couldn't be parsed</span>
        </div>
        <p className="text-xs text-muted-foreground">Scroll up to "Clip 1 / 2 / 3..." in the body text — copy any caption you like into the Video Clip prompt below to render it manually. (If you see this every time on the same post, regenerate it via the Podcast template so the section headers are clean.)</p>
      </div>
    );
  }

  const startClip = async (clip) => {
    if (userPlan === "free") {
      toast.error("Reel generation is a Pro+ feature. Upgrade to turn clip ideas into actual videos.");
      return;
    }
    const idx = clip.index;
    setJobs((j) => ({ ...j, [idx]: { status: "queued", url: "", error: "" } }));

    // Build a Sora 2 prompt from the clip caption + parent topic. Capped at 1000
    // chars (Sora 2 API limit).
    const topic = content?.topic || "podcast clip";
    const caption = clip.caption || `Highlight from ${topic}`;
    const rationale = clip.rationale ? ` Tone/angle: ${clip.rationale}.` : "";
    const prompt = `Short-form vertical reel from a podcast about: ${topic}. Visual focus: ${caption}.${rationale}`.slice(0, 1000);

    try {
      const res = await axios.post(`${API}/videos/generate`, {
        prompt,
        size: "vertical",
        duration: 4,
        model: "sora-2",
      }, { headers: { Authorization: `Bearer ${token}` } });

      const jobId = res.data.job_id;
      toast.success(`Clip ${idx} queued — Sora 2 is rendering it.`);
      setJobs((j) => ({ ...j, [idx]: { ...j[idx], status: "processing", jobId } }));

      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const j = await axios.get(`${API}/videos/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
          const job = j.data;
          if (job.status === "completed" && job.video_url) {
            const full = `${API.replace(/\/api$/, "")}${job.video_url}`;
            setJobs((s) => ({ ...s, [idx]: { status: "completed", url: full, error: "", jobId } }));
            toast.success(`Clip ${idx} ready!`);
            return;
          }
          if (job.status === "failed") {
            setJobs((s) => ({ ...s, [idx]: { status: "failed", url: "", error: job.error || "Generation failed", jobId } }));
            toast.error(`Clip ${idx}: ${job.error || "generation failed"}`);
            return;
          }
          setJobs((s) => ({ ...s, [idx]: { ...s[idx], status: job.status || "processing" } }));
          if (attempts < 36) setTimeout(poll, 5000);
          else setJobs((s) => ({ ...s, [idx]: { ...s[idx], status: "failed", error: "Timed out — check /dashboard/videos" } }));
        } catch (e) {
          setJobs((s) => ({ ...s, [idx]: { ...s[idx], status: "failed", error: e.response?.data?.detail || "Status fetch failed" } }));
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      setJobs((j) => ({ ...j, [idx]: { status: "failed", url: "", error: e.response?.data?.detail || "Failed to queue clip" } }));
      toast.error(e.response?.data?.detail || "Failed to queue clip");
    }
  };

  return (
    <div className="px-4 py-3 border-t border-border bg-white/5" data-testid="podcast-clip-reels">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium">One-click Reels from your clip suggestions</span>
          <Badge variant="secondary" className="text-[10px]">Sora 2 · 9:16 · 4s</Badge>
        </div>
        <Link to="/dashboard/videos" className="text-xs text-cyan-400 hover:underline">All renders →</Link>
      </div>

      <div className="grid gap-2">
        {clips.map((clip) => {
          const job = jobs[clip.index] || {};
          const busy = job.status === "queued" || job.status === "processing";
          const done = job.status === "completed" && job.url;
          const failed = job.status === "failed";
          return (
            <div key={clip.index} className="p-3 rounded-lg bg-black/20 border border-white/5 flex flex-col gap-2" data-testid={`clip-row-${clip.index}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-cyan-500/80 text-white text-[10px]">Clip {clip.index}</Badge>
                    {clip.range && <span className="text-xs text-cyan-300 font-mono">{clip.range}</span>}
                  </div>
                  {clip.caption && <p className="text-sm text-foreground/90 leading-snug">{clip.caption}</p>}
                  {clip.rationale && <p className="text-xs text-muted-foreground mt-1 italic">{clip.rationale}</p>}
                </div>
                {!done && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || userPlan === "free"}
                    onClick={() => startClip(clip)}
                    className="text-cyan-400 border-cyan-400/30"
                    data-testid={`generate-clip-${clip.index}`}
                  >
                    {busy ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Rendering…</> :
                     failed ? <><Sparkles className="w-3 h-3 mr-1" />Retry</> :
                     userPlan === "free" ? <><Video className="w-3 h-3 mr-1" />Upgrade</> :
                     <><Video className="w-3 h-3 mr-1" />Generate Reel</>}
                  </Button>
                )}
              </div>

              {done && (
                <div className="relative rounded-md overflow-hidden bg-black/40" data-testid={`clip-video-${clip.index}`}>
                  <video src={job.url} controls className="w-full max-h-72 mx-auto" />
                  <a href={job.url} download={`creatara-clip-${clip.index}.mp4`} className="absolute top-2 right-2">
                    <Button size="sm" className="h-7 bg-black/60 hover:bg-black/80 text-white">
                      <Download className="w-3 h-3 mr-1" />Download
                    </Button>
                  </a>
                </div>
              )}

              {failed && !busy && (
                <p className="text-xs text-violet-400">{job.error}</p>
              )}
            </div>
          );
        })}
      </div>

      {userPlan === "free" && (
        <p className="text-xs text-muted-foreground mt-3">
          Free plan ships the clip ideas. <Link to="/dashboard/settings" className="underline text-cyan-400">Upgrade to Pro</Link> to render them as Sora 2 reels in one click.
        </p>
      )}
    </div>
  );
}
